"use client";

import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { BellRing } from "lucide-react";

import { VehicleEventToast } from "@/components/notifications/VehicleEventToast";

import { useAuth } from "@/contexts/AuthContext";
import { useVehicleEventContext } from "@/hooks/use-vehicle-event-notifications";
import { isSoundEnabled, isToastEnabled } from "@/lib/api/notification-preferences";
import { queryKeys, useNotificationPrefsQuery } from "@/lib/api/queries";
import { playNotificationBeep } from "@/lib/notifications/beep";
import {
  describeVehicleEvent,
  isFuelEventType,
  isHiddenEventType,
  parseVehicleEvent,
  vehicleEventSeverity,
} from "@/lib/notifications/vehicle-events";
import { subscribeDeviceUpdates, type DeviceUpdateEnvelope } from "@/lib/realtime/vehicle-socket";
import { trackingFollowHref } from "@/lib/notifications/links";
import { clearVehicleAlerts, recordVehicleUpdate } from "@/lib/notifications/vehicle-alert-store";
import {
  areVehicleToastsCentered,
  subscribeVehicleToastPlacement,
} from "@/lib/notifications/toast-placement";

/** Events are batched briefly so a burst (a tab waking up, a fleet crossing one zone) toasts once. */
const EVENT_FLUSH_MS = 1000;

/**
 * Sonner's icon slot is a fixed 16px box that also nudges its svg sideways; the tinted chip needs
 * 24px and no offset. Scoped to these toasts so every other toast keeps its stock layout.
 */
const ICON_SLOT_CLASS = "size-6! [&_svg]:mx-0!";

/**
 * `VehicleEventToast` draws its own rounded card. The Toaster's global `toast` class would still paint
 * Sonner's square wrapper behind it — background, border and shadow — so a rounded accent border sat
 * on a square box. Cleared here, for these toasts only.
 */
const CUSTOM_TOAST_WRAPPER_CLASS = "bg-transparent! border-0! shadow-none! p-0!";

/** Above this many new events in one batch, a single summary toast replaces the individual ones. */
const MAX_INDIVIDUAL_TOASTS = 5;

/**
 * How long after a live event the stored history is refetched. Upstream writes the
 * `ClientNotification.php` row from the same device report, but not necessarily before the socket
 * delivers it, so an immediate refetch could miss it.
 */
const HISTORY_REFRESH_DELAY_MS = 3000;

/** Event ids remembered so a redelivered update is not toasted twice. Oldest are dropped first. */
const MAX_REMEMBERED_EVENTS = 500;

/** Where the feed used to live before upstream kept the history; cleared once on mount. */
const LEGACY_EVENT_STORAGE_PREFIX = "fleettrack-vehicle-events:";

interface PendingEvent {
  /** `${vehicleId}|${point ts}|${raw}`, the dedupe key. */
  id: string;
  vehicleId: string;
  raw: string;
  occurredAt: string;
  speed?: number;
  /** `id:5`, `c:123`, `""` for no driver; absent when the update didn't say. */
  driverRef?: string;
}

function clearLegacyEventStorage() {
  try {
    const keys: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key?.startsWith(LEGACY_EVENT_STORAGE_PREFIX)) keys.push(key);
    }
    for (const key of keys) window.localStorage.removeItem(key);
  } catch {
    // Blocked storage has nothing of ours in it either.
  }
}

/**
 * Where the tracked point happened. The device's own `ts` is preferred — it is also what makes the
 * dedupe key stable across a redelivery — then the envelope's, then arrival time.
 */
function eventTimestamp(envelope: DeviceUpdateEnvelope): { key: string; iso: string } {
  const deviceTs = envelope.payload.ts;
  if (deviceTs) {
    const ms = Date.parse(deviceTs);
    if (Number.isFinite(ms)) return { key: deviceTs, iso: new Date(ms).toISOString() };
  }
  if (typeof envelope.ts === "number" && Number.isFinite(envelope.ts)) {
    const iso = new Date(envelope.ts).toISOString();
    return { key: String(envelope.ts), iso };
  }
  const iso = new Date().toISOString();
  return { key: iso, iso };
}

/**
 * Turns the live `events` on each vehicle update into toasts, app-wide. The history behind the
 * notifications page and the bell is upstream's (`ClientNotification.php`); this only nudges those
 * queries to refetch when something new happened.
 *
 * Mounted once in `Providers` rather than in `DashboardLayout`, which every view renders for
 * itself and so remounts on each navigation. Renders nothing. Everything the handlers read —
 * preferences, lookup lists, translations — goes through refs so the socket subscription depends
 * on the token alone and survives a language switch or a list refresh.
 */
export function VehicleEventListener() {
  const { token, user } = useAuth();
  const router = useRouter();
  const queryClient = useQueryClient();
  const prefsQuery = useNotificationPrefsQuery();
  const context = useVehicleEventContext();

  useEffect(() => {
    clearLegacyEventStorage();
  }, []);

  const contextRef = useRef(context);
  const toastPrefsRef = useRef(prefsQuery.data?.toast);
  const showFuel = user?.features.fuel ?? true;
  const showFuelRef = useRef(showFuel);
  const soundPrefsRef = useRef(prefsQuery.data?.sound);
  const routerRef = useRef(router);
  useEffect(() => {
    contextRef.current = context;
    toastPrefsRef.current = prefsQuery.data?.toast;
    soundPrefsRef.current = prefsQuery.data?.sound;
    showFuelRef.current = showFuel;
    routerRef.current = router;
  }, [context, prefsQuery.data, router, showFuel]);

  /** The vehicle-event toasts in the default corner now, so centring can clear exactly these. */
  const shownToastIdsRef = useRef(new Set<string | number>());

  // Following a vehicle moves new toasts to the bottom centre (`toast-placement.ts`). The ones already
  // in the corner go, or they would keep covering the followed-vehicle panel until they time out.
  // Other toasts stay.
  useEffect(() => {
    const shown = shownToastIdsRef.current;
    return subscribeVehicleToastPlacement(() => {
      if (!areVehicleToastsCentered()) return;
      for (const id of shown) toast.dismiss(id);
      shown.clear();
    });
  }, []);

  useEffect(() => {
    if (!token || typeof window === "undefined") return;

    const shown = shownToastIdsRef.current;
    const forget = (item: { id: string | number }) => {
      shown.delete(item.id);
    };

    let pending: PendingEvent[] = [];
    let flushTimer: number | null = null;
    let historyTimer: number | null = null;
    // Per connection: a new token is a new session, with nothing toasted yet.
    const remembered = new Set<string>();

    /** The events not toasted before, remembered from now on. */
    const takeNew = (batch: PendingEvent[]): PendingEvent[] => {
      const added: PendingEvent[] = [];
      for (const event of batch) {
        if (remembered.has(event.id)) continue;
        remembered.add(event.id);
        added.push(event);
      }
      // A Set iterates in insertion order, so the first entries are the oldest.
      for (const id of remembered) {
        if (remembered.size <= MAX_REMEMBERED_EVENTS) break;
        remembered.delete(id);
      }
      return added;
    };

    /** Debounced: a burst of events refetches the history once, after upstream has stored it. */
    const scheduleHistoryRefresh = () => {
      if (historyTimer !== null) return;
      historyTimer = window.setTimeout(() => {
        historyTimer = null;
        void queryClient.invalidateQueries({ queryKey: queryKeys.notifications });
      }, HISTORY_REFRESH_DELAY_MS);
    };

    const showToasts = (added: PendingEvent[]) => {
      const ctx = contextRef.current;
      // Read per batch: a toast stays where it opened, only new ones follow the switch.
      const centered = areVehicleToastsCentered();
      const position = centered ? ("bottom-center" as const) : undefined;
      const track = (id: string | number) => {
        if (!centered) shown.add(id);
      };

      if (added.length > MAX_INDIVIDUAL_TOASTS) {
        const summaryId = toast.info(`${added.length} ${ctx.t.vehicleEvents.newNotifications}`, {
          icon: (
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-primary/15 text-primary">
              <BellRing className="h-3.5 w-3.5" strokeWidth={2.25} />
            </span>
          ),
          classNames: { toast: "border-l-4 border-l-primary", icon: ICON_SLOT_CLASS },
          action: {
            label: ctx.t.vehicleEvents.view,
            onClick: () => routerRef.current.push("/notifications"),
          },
          position,
          onDismiss: forget,
          onAutoClose: forget,
        });
        track(summaryId);
        return;
      }

      for (const entry of added) {
        const parsed = parseVehicleEvent(entry.raw);
        if (!parsed) continue;
        const { title, detail, vehicleName } = describeVehicleEvent(entry.vehicleId, parsed, ctx, {
          speed: entry.speed,
        });
        toast.custom(
          (toastId) => (
            <VehicleEventToast
              kind={parsed.type}
              severity={vehicleEventSeverity(parsed.type)}
              label={title}
              detail={detail}
              vehicle={ctx.vehiclesById.get(entry.vehicleId)}
              fallbackVehicleName={vehicleName}
              driverRef={entry.driverRef}
              driverNamesById={ctx.driverNamesById}
              actionLabel={ctx.t.vehicleEvents.view}
              onAction={() => {
                toast.dismiss(toastId);
                routerRef.current.push(trackingFollowHref(entry.vehicleId));
              }}
            />
          ),
          {
            id: entry.id,
            classNames: { toast: CUSTOM_TOAST_WRAPPER_CLASS },
            position,
            onDismiss: forget,
            onAutoClose: forget,
          },
        );
        track(entry.id);
      }
    };

    const flush = () => {
      flushTimer = null;
      const batch = pending;
      pending = [];
      // Only genuinely new events are toasted; a redelivered update is dropped here.
      const added = takeNew(batch);
      if (added.length === 0) return;
      showToasts(added);
      // One beep per batch, however many of its events are unmuted.
      const sound = soundPrefsRef.current;
      if (added.some((entry) => isSoundEnabled(sound, parseVehicleEvent(entry.raw)?.type ?? ""))) {
        playNotificationBeep();
      }
    };

    const unsubscribe = subscribeDeviceUpdates(token, (envelope) => {
      const rawEvents = envelope.payload.events;
      const events = Array.isArray(rawEvents)
        ? rawEvents.filter((raw): raw is string => typeof raw === "string")
        : [];
      const parsedEvents = events.flatMap((raw) => {
        const parsed = parseVehicleEvent(raw);
        return parsed ? [{ raw, parsed }] : [];
      });

      const { key, iso } = eventTimestamp(envelope);
      const { speed: rawSpeed } = envelope.payload;

      // Every update, every event, before the preference filter: a closing type the user muted must
      // still close its condition. The point's own speeding flags and speed also close a speeding
      // condition whose `*_end` never came; those ends come back to be toasted like real ones.
      const healedEnds = recordVehicleUpdate(
        envelope.deviceId,
        parsedEvents.map((entry) => entry.parsed),
        {
          isSpeeding: envelope.payload.is_speeding,
          isStreetSpeeding: envelope.payload.is_street_speeding,
          speed: typeof rawSpeed === "number" && Number.isFinite(rawSpeed) ? rawSpeed : undefined,
          at: Date.parse(iso),
        },
      );
      for (const parsed of healedEnds) {
        parsedEvents.push({ raw: parsed.arg ? `${parsed.type}:${parsed.arg}` : parsed.type, parsed });
      }

      if (parsedEvents.length === 0) return;
      // Upstream stores every real event, toasted or not; healed ends are ours alone, but a
      // refetch for them is harmless.
      scheduleHistoryRefresh();

      const prefs = toastPrefsRef.current;
      const { speed, driver_id: driverId } = envelope.payload;
      const point = {
        speed: typeof speed === "number" && Number.isFinite(speed) ? speed : undefined,
        // `null` is "no driver"; only a missing field means "unknown, use the cached vehicle".
        driverRef: driverId === undefined ? undefined : (driverId ?? ""),
      };

      for (const { raw, parsed } of parsedEvents) {
        // A type switched off is not toasted; upstream still lists and counts it.
        if (!isToastEnabled(prefs, parsed.type)) continue;
        if (isHiddenEventType(parsed.type)) continue;
        if (!showFuelRef.current && isFuelEventType(parsed.type)) continue;
        pending.push({
          id: `${envelope.deviceId}|${key}|${raw}`,
          vehicleId: envelope.deviceId,
          raw,
          occurredAt: iso,
          ...point,
        });
      }

      if (pending.length > 0 && flushTimer === null) {
        flushTimer = window.setTimeout(flush, EVENT_FLUSH_MS);
      }
    });

    return () => {
      unsubscribe();
      if (flushTimer !== null) window.clearTimeout(flushTimer);
      if (historyTimer !== null) window.clearTimeout(historyTimer);
      // Logout or a new login: one account's live state must not show under another.
      clearVehicleAlerts();
    };
  }, [token, queryClient]);

  return null;
}
